<template>
  <div class="t360-state" v-if="loading"><my-loader /></div>
  <div v-else-if="error" class="t360-state t360-error">{{ error }}</div>
  <template v-else-if="trip">

    <div v-if="mode === MODE_COMPACT" class="t360-compact">
      <div class="t360-row">
        <span class="t360-status-pill" :class="statusClass(trip.status_name)">{{ trip.status_name || '—' }}</span>
        <span v-if="trip.is_overdue" class="badge badge-red">Просрочен</span>
        <span v-if="trip.is_carousel" class="badge badge-purple">Карусель</span>
      </div>
      <div class="t360-row t360-route">
        <strong>{{ trip.city_out || '—' }} → {{ trip.city_in || '—' }}</strong>
      </div>
      <div class="t360-compact-grid">
        <div class="field"><span class="f-label">Заказчик</span><span class="f-val">{{ dash(trip.client_name) }}</span></div>
        <div class="field"><span class="f-label">Логист</span><span class="f-val">{{ dash(trip.logist) }}</span></div>
        <div class="field"><span class="f-label">Ставка заказчика</span><span class="f-val">{{ fmtMoney(trip.customer_gross) }}</span></div>
        <div class="field"><span class="f-label">Ставка перевозчика</span><span class="f-val">{{ fmtMoney(trip.carrier_price_eff) }}</span></div>
        <div class="field"><span class="f-label">Маржа</span><span class="f-val">{{ fmtPct(trip.margin_pct) }}</span></div>
        <div class="field"><span class="f-label">Комиссия</span><span class="f-val">{{ fmtMoney(trip.commission_rub) }}</span></div>
        <div class="field"><span class="f-label">Площадка</span><span class="f-val">{{ dash(trip.service_name) }}</span></div>
        <div class="field"><span class="f-label">UID</span><span class="f-val">{{ dash(trip.uid) }}</span></div>
        <div class="field"><span class="f-label">Рейс в ERP</span><span class="f-val">{{ dash(trip.erp_trip_id) }}</span></div>
        <div class="field"><span class="f-label">Тип ТС</span><span class="f-val">{{ dash(trip.veh_type) }}</span></div>
        <div class="field"><span class="f-label">Поиск</span><span class="f-val">{{ fmtHours(trip.search_duration_sec) }}</span></div>
        <div class="field"><span class="f-label">Ответственный</span><span class="f-val">{{ dash(trip.responsible_name) }}</span></div>
      </div>
      <div class="t360-row t360-dates">
        <span>Загрузка: <strong>{{ fmtDate(trip.departure_planed_dt) }}</strong></span>
        <span>Разгрузка: <strong>{{ fmtDate(trip.destination_planed_dt) }}</strong></span>
        <span>Создан: <strong>{{ fmtDate(trip.order_create_dt) }}</strong></span>
      </div>
      <div class="t360-row t360-flags">
        <span v-if="trip.win_is_bot === true" class="badge badge-purple">Победа: бот</span>
        <span v-else-if="trip.win_is_bot === false" class="badge badge-gray">Победа: человек</span>
        <span v-if="trip.has_erp_pair" class="badge badge-green">Заведён в ERP</span>
        <span v-else class="badge badge-gray">Не заведён в ERP</span>
      </div>
    </div>

    <template v-else>
      <div class="t360-tabs">
        <button
          v-for="tab in TABS"
          :key="tab.id"
          type="button"
          class="t360-tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >{{ tab.label }}</button>
        <button
          v-if="isLiveTab"
          type="button"
          class="t360-tab-refresh"
          :class="{ loading: liveTabLoading }"
          :disabled="liveTabLoading"
          :title="liveTabLoading ? 'Загрузка…' : 'Обновить данные вкладки'"
          @click="reloadLiveTab"
        >⟳</button>
      </div>
      <div class="t360-links">
        <button type="button" class="link-chip" title="UID — связка: торги ↔ карточка распределения (клик — копировать)" @click="copyVal(trip.uid)">
          uid: {{ dash(trip.uid) }}
        </button>
        <button type="button" class="link-chip" title="Номер доставки — связка: карточка ↔ рейс ERP (клик — копировать)" @click="copyVal(trip.distr_id)">
          карточка: {{ dash(trip.distr_id) }}
        </button>
        <button v-if="trip.erp_trip_id" type="button" class="link-chip" title="trip_id в ERP/TMS (клик — копировать)" @click="copyVal(trip.erp_trip_id)">
          erp: {{ trip.erp_trip_id }}
        </button>
        <button v-if="trip.order_trip_id" type="button" class="link-chip" title="trip_id на площадке (клик — копировать)" @click="copyVal(trip.order_trip_id)">
          площадка: {{ trip.order_trip_id }}
        </button>
      </div>
      <div class="t360-tabpane">
        <div v-if="activeTab === 'timeline'" class="tl-wrap">
          <template v-if="timelineEvents.length">
            <div v-if="timelineLegend.length" class="tl-legend">
              <span
                v-for="group in timelineLegend"
                :key="group.label"
                class="tl-legend-item"
              >
                <span class="tl-legend-sq" :style="{ background: group.color }"></span>
                {{ group.label }}
              </span>
            </div>
            <div class="tl-scale" @mouseleave="hoveredIdx = -1">
              <span class="tl-scale-min">{{ fmtTs(timelineEvents[0].ts) }}</span>
              <div
                v-for="(seg, si) in timelineSegments"
                :key="'seg' + si"
                class="tl-seg"
                :style="{ left: seg.left + '%', width: seg.width + '%', background: seg.color }"
                :title="seg.label"
              ></div>
              <div
                v-for="(event, idx) in timelineEvents"
                :key="'tick' + idx"
                class="tl-tick"
                :class="{ active: hoveredIdx === idx }"
                :style="{ left: event._pct + '%', background: timelineColor(event) }"
                :title="event.label + ' · ' + fmtTs(event.ts)"
                @mouseenter="hoveredIdx = idx"
              ></div>
              <span class="tl-scale-max">{{ fmtTs(timelineEvents[timelineEvents.length - 1].ts) }}</span>
            </div>
            <div v-for="(event, idx) in timelineEvents" :key="idx" class="tl-item"
              :class="{ hovered: hoveredIdx === idx }"
              @mouseenter="hoveredIdx = idx"
              @mouseleave="hoveredIdx = -1"
            >
              <div class="tl-rail">
                <span class="tl-dot" :style="{ background: timelineColor(event) }"></span>
                <span v-if="idx < timelineEvents.length - 1" class="tl-line"></span>
              </div>
              <div class="tl-content">
                <div class="tl-head">
                  <span class="tl-label">{{ event.label }}</span>
                  <span class="tl-dt">{{ fmtTs(event.ts) }}</span>
                  <span v-if="idx > 0" class="tl-gap">+{{ gapLabel(timelineEvents[idx - 1], event) }}</span>
                </div>
                <div v-if="eventMeta(event)" class="tl-meta">{{ eventMeta(event) }}</div>
              </div>
            </div>
          </template>
          <div v-else class="tl-empty">Нет событий таймлайна для этой карточки</div>
        </div>

        <div v-else-if="activeTab === 'route'" class="route-pane">
          <div class="t360-section-title">Расчёт стоимости направления</div>
          <div v-if="routeLoading && !priceLookup" class="t360-live-state"><my-loader /></div>
          <template v-else-if="priceLookup">
            <div class="pr-head">
              <strong>{{ priceLookup.departure_city }} → {{ priceLookup.destination_city }}</strong>
              <span v-if="priceLookup.distance" class="pr-dist">{{ priceLookup.distance }} км</span>
              <span v-if="priceLookup.source === 'direct'" class="badge badge-green">цена найдена</span>
              <span v-else class="badge badge-gray">нет цены</span>
              <span v-if="priceLookup.is_priority" class="badge badge-purple">priority</span>
            </div>
            <div v-if="routeMismatch" class="pr-warn">Расчёт по маршруту ATI №{{ priceLookup.ati_route_id }} — не совпадает с маршрутом рейса №{{ trip.ati_route_id }}</div>
            <div class="pr-chips">
              <button
                v-for="chip in TRANSPORT_CHIPS"
                :key="chip.key"
                type="button"
                class="pr-chip"
                :class="{ active: routeTransport === chip.key }"
                @click="routeTransport = chip.key"
              >{{ chip.label }}</button>
            </div>
            <div v-if="featuredEntry" class="pr-hero" :class="{ 'pr-hero-derived': featuredDerived }">
              <div class="pr-hero-main">
                <div class="pr-hero-label">Ставка {{ featuredLabel.toLowerCase() }} (за км)</div>
                <div class="pr-hero-value">{{ fmtPrice(featuredPrice) }} <span class="pr-hero-unit">₽/км</span></div>
                <div class="pr-hero-sub">без НДС: {{ fmtPrice(featuredWithoutNds) }} ₽/км</div>
                <div class="pr-hero-note">{{ featuredDerived ? 'расчёт из тента' : 'реальная ставка ATI' }}</div>
              </div>
              <div v-if="totalCost != null" class="pr-hero-total">
                <div class="pr-hero-label">Полная стоимость маршрута</div>
                <div class="pr-hero-big">{{ fmtPrice(totalCost) }} ₽</div>
                <div class="pr-hero-sub">без НДС: {{ fmtPrice(totalCostWithoutNds) }} ₽</div>
                <div class="pr-hero-sub">{{ fmtPrice(featuredPrice) }} × {{ priceLookup.distance }} км</div>
              </div>
            </div>
            <div class="pr-grid">
              <div
                v-for="cell in priceCells"
                :key="cell.key"
                class="pr-cell"
                :class="{ 'pr-cell-derived': cell.entry && cell.entry.is_derived }"
              >
                <div class="pr-cell-top">
                  <span>{{ cell.label }}</span>
                  <span v-if="cell.entry" class="pr-cell-tag">{{ cell.entry.is_derived ? 'расчёт' : 'реальная' }}</span>
                </div>
                <div v-if="cell.entry" class="pr-cell-body">
                  <div class="pr-line"><span>мин</span><b>{{ fmtPrice(cell.entry.min_volume_with_nds) }}</b><small>/{{ fmtPrice(cell.entry.min_volume_without_nds) }}</small></div>
                  <div class="pr-line"><span>сред</span><b>{{ fmtPrice(cell.entry.avg_volume_with_nds) }}</b><small>/{{ fmtPrice(cell.entry.avg_volume_without_nds) }}</small></div>
                  <div class="pr-line"><span>макс</span><b>{{ fmtPrice(cell.entry.max_volume_with_nds) }}</b><small>/{{ fmtPrice(cell.entry.max_volume_without_nds) }}</small></div>
                </div>
                <div v-else class="pr-cell-empty">нет данных</div>
              </div>
            </div>
            <div class="pr-meta">
              <span v-if="priceLookup.report_date">📅 {{ fmtRouteDate(priceLookup.report_date) }}</span>
              <span v-if="priceLookup.loads_count != null">🚚 {{ priceLookup.loads_count }} рейсов</span>
              <span v-if="priceLookup.confidence != null">🎯 {{ priceLookup.confidence }}</span>
              <span v-if="priceLookup.ati_route_id">№ {{ priceLookup.ati_route_id }}</span>
              <a v-if="canYandexLink" :href="yandexLink" target="_blank" rel="noopener" class="pr-link">🗺 Яндекс.Карты ↗</a>
            </div>
            <div v-if="priceLookup.last_refresh" class="pr-refresh">
              Обновление цены: {{ priceLookup.last_refresh.bucket }} · {{ refreshStatusLabel }}
              <span v-if="priceLookup.last_refresh.message"> — {{ priceLookup.last_refresh.message }}</span>
            </div>
          </template>
          <div v-else-if="priceLookupError" class="t360-live-state t360-error">{{ priceLookupError }}</div>
          <div v-else-if="!hasCities" class="t360-live-empty">Города маршрута не заданы — расчёт недоступен</div>

          <div v-if="baseRate" class="pr-base">
            <div class="pr-base-item">
              <span class="pr-hero-label">Базовая ставка заказчика</span>
              <b>{{ fmtPrice(baseRate.cost_with_nds) }} ₽</b>
              <span class="pr-hero-sub">без НДС {{ fmtPrice(baseRate.cost_without_nds) }} ₽</span>
            </div>
            <div v-if="baseMargin != null" class="pr-base-item">
              <span class="pr-hero-label">Маржа ATI vs база</span>
              <b :class="baseMargin >= 0 ? 'pr-margin-pos' : 'pr-margin-neg'">{{ baseMargin >= 0 ? '+' : '' }}{{ baseMargin }}%</b>
              <span class="pr-hero-sub">{{ fmtPrice(baseRate.cost_with_nds - totalCost) }} ₽</span>
            </div>
          </div>
          <div v-else-if="baseRateForbidden" class="t360-live-empty">Нет прав на просмотр базовых ставок</div>
          <div v-else-if="!routeLoading && hasCities && priceLookup" class="t360-live-empty">Базовая ставка для направления не задана</div>

          <div class="t360-section-title">Применённые правила бота</div>
          <div v-if="!trip.uid" class="t360-live-empty">Нет привязки к торгам (uid)</div>
          <template v-else>
            <div v-if="routeLoading && !pureOrder" class="t360-live-state"><my-loader /></div>
            <div v-else-if="pureOrderError" class="t360-live-state t360-error">{{ pureOrderError }}</div>
            <div v-else-if="!pureOrder" class="t360-live-empty">Торги не найдены</div>
            <template v-else>
              <div v-if="botTask" class="pr-task">
                Задача бота: <b>{{ fmtPrice(botTask.value) }} ₽</b>
                <span :class="['badge', botTask.active ? 'badge-on' : 'badge-off']">{{ botTask.active ? 'активна' : 'не активна' }}</span>
              </div>
              <AdviseRules v-if="pureOrderAdvise.length" :advise="pureOrderAdvise" />
              <div v-else class="t360-live-empty">Препрайсинг не рассчитан</div>
            </template>
          </template>

          <div class="rp-head">
            <div class="t360-section-title rp-title-block">Точки маршрута</div>
            <span v-if="latePointsCount" class="badge badge-red">Опозданий: {{ latePointsCount }}</span>
            <button v-if="trip.has_erp_pair" type="button" class="rp-dt-btn" title="Перейти на вкладку «Простой»" @click="activeTab = 'downtime'">Простои →</button>
          </div>
          <div v-if="pointsLiveLoading" class="t360-live-state"><my-loader /></div>
          <template v-else-if="routePointsModel.length">
            <div v-for="(pt, pi) in routePointsModel" :key="pt.key" class="rp-item">
              <div class="rp-rail">
                <span class="rp-dot" :class="pt.is_loading ? 'rp-dot-load' : 'rp-dot-unload'"></span>
                <span v-if="pi < routePointsModel.length - 1" class="rp-line"></span>
              </div>
              <div class="rp-content">
                <div class="rp-title-row">
                  <span class="rp-title">{{ pt.is_loading ? 'Погрузка' : 'Разгрузка' }}<template v-if="pt.city"> · {{ pt.city }}</template></span>
                  <span v-if="pt.plannedLabel" class="rp-plan">{{ pt.plannedLabel }}</span>
                  <span v-if="pt.late" class="badge badge-red">{{ pt.delayLabel }}</span>
                </div>
                <div v-if="pt.warehouse" class="rp-meta">Склад: {{ pt.warehouse }}</div>
                <div v-if="pt.address" class="rp-meta">{{ pt.address }}</div>
                <div v-if="pt.contacts" class="rp-meta">{{ pt.contacts }}</div>
                <div v-if="pt.factParts.length" class="rp-fact">
                  <span v-for="f in pt.factParts" :key="f.label" class="rp-fact-item">{{ f.label }}: <b>{{ f.value }}</b></span>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="t360-live-empty">Точек маршрута нет</div>
        </div>

        <div v-else-if="activeTab === 'summary'" class="t360-fields">
          <div class="t360-section-title">Маршрут и груз</div>
          <div class="t360-row t360-route">
            <strong>{{ trip.city_out || '—' }} → {{ trip.city_in || '—' }}</strong>
          </div>
          <div class="field"><span class="f-label">Загрузка</span><span class="f-val">{{ fmtDate(trip.departure_planed_dt) }}</span></div>
          <div class="field"><span class="f-label">Разгрузка</span><span class="f-val">{{ fmtDate(trip.destination_planed_dt) }}</span></div>
          <div class="field cw-field">
            <span class="f-label">Закрытие</span>
            <span class="f-val">
              <div v-if="closeModel" class="cw-widget" :title="closeModel.tooltip">
                <div class="cw-track">
                  <div class="cw-bar" :class="closeModel.barCls" :style="{ width: closeModel.barPct + '%' }"></div>
                </div>
                <div v-if="closeModel.markPct != null" class="cw-mark" :style="{ left: closeModel.markPct + '%' }"></div>
                <div v-if="closeModel.mark12Pct != null" class="cw-mark cw-mark-12" :style="{ left: closeModel.mark12Pct + '%' }"></div>
              </div>
              <span v-else class="cw-empty">—</span>
            </span>
          </div>
          <div class="field"><span class="f-label">Тип ТС / тоннаж</span><span class="f-val">{{ dash(trip.veh_type) }} / {{ dash(trip.veh_tonnage) }}</span></div>
          <div class="field"><span class="f-label">Груз</span><span class="f-val">{{ dash(trip.cargo_list) }}<template v-if="trip.cargo_weight"> · {{ dash(trip.cargo_weight) }} т</template></span></div>

          <div class="t360-section-title">Контрагенты</div>
          <div class="field"><span class="f-label">Заказчик (торги)</span><span class="f-val">{{ dash(trip.client_name) }}</span></div>
          <div class="field"><span class="f-label">Заказчик (ERP)</span><span class="f-val">{{ dash(trip.customer_name) }}</span></div>
          <div class="field"><span class="f-label">Перевозчик (TMS)</span><span class="f-val">{{ dash(trip.tms_carrier_name) }}</span></div>
          <div class="field"><span class="f-label">Менеджер / Логист</span><span class="f-val">{{ dash(trip.closer_name) }} / {{ dash(trip.worker_name) }}</span></div>
          <div class="field"><span class="f-label">Ответственный</span><span class="f-val">{{ dash(trip.responsible_name) }}</span></div>

          <div class="t360-section-title">Статусы и сроки</div>
          <div class="field"><span class="f-label">Карточка</span><span class="f-val"><span class="t360-status-pill" :class="statusClass(trip.status_name)">{{ trip.status_name || '—' }}</span></span></div>
          <div class="field"><span class="f-label">Статус в ERP</span><span class="f-val">{{ dash(trip.work_status_name) }}</span></div>
          <div class="field"><span class="f-label">Поиск / В поиске</span><span class="f-val">{{ fmtHours(trip.search_duration_sec) }} / {{ fmtHours(trip.ongoing_search_sec) }}</span></div>
          <div class="field"><span class="f-label">Участников / Просрочено</span><span class="f-val">{{ dash(trip.participants_count) }} / {{ dash(trip.expired_count) }}</span></div>
          <div class="field"><span class="f-label">Создан на торгах</span><span class="f-val">{{ fmtDate(trip.order_create_dt) }}</span></div>
          <div class="field"><span class="f-label">Карточка заведена</span><span class="f-val">{{ fmtDate(trip.card_create_dt) }}</span></div>
          <div class="field"><span class="f-label">Завершена</span><span class="f-val">{{ fmtDate(endedAt) }}</span></div>

          <div class="t360-flags">
            <flag-badge :value="trip.win_is_bot" label="Выиграно ботом" />
            <flag-badge :value="trip.is_carousel" label="Карусель" />
            <flag-badge :value="trip.is_overdue" label="Просрочен" />
            <flag-badge :value="trip.has_erp_pair" label="Заведён в ERP" />
          </div>
        </div>

        <div v-else-if="activeTab === 'finance'" class="fin-pane">
          <div class="fin-metrics-grid">
            <button
              v-for="item in financeMetrics"
              :key="item.label"
              type="button"
              class="fin-metric"
              :class="{ 'fin-metric-soft': item.highlight }"
              :title="item.title"
              @click="copyVal(item.copyValue)"
            >
              <span class="fin-metric-title">
                <span>{{ item.label }}</span>
                <span v-if="item.sub" class="f-sub">{{ item.sub }}</span>
              </span>
              <strong>{{ item.value }}</strong>
            </button>
          </div>
          <p class="fin-hint">Показатели в порядке карточки калькулятора. Клик по плитке — скопировать значение.</p>
        </div>

        <div v-else-if="activeTab === 'bidding'" class="t360-fields">
          <div class="field"><span class="f-label">UID</span><span class="f-val">{{ dash(trip.uid) }}</span></div>
          <div class="field"><span class="f-label">Площадка</span><span class="f-val">{{ dash(trip.service_name) }}</span></div>
          <div class="field"><span class="f-label">Клиент</span><span class="f-val">{{ dash(trip.client_name) }}</span></div>
          <div class="field"><span class="f-label">ID в торговой системе</span><span class="f-val">{{ dash(trip.meta_id) }}</span></div>
          <div class="field"><span class="f-label">ID на площадке</span><span class="f-val">{{ dash(trip.order_trip_id) }}</span></div>
          <div class="field"><span class="f-label">Ставка (с НДС)</span><span class="f-val">{{ moneyRange(trip.cost_start_with_nds, trip.cost_last_with_nds) }}</span></div>
          <div class="field"><span class="f-label">Ставка (без НДС)</span><span class="f-val">{{ moneyRange(trip.cost_start_without_nds, trip.cost_last_without_nds) }}</span></div>
          <div class="field"><span class="f-label">Создан</span><span class="f-val">{{ fmtDate(trip.order_create_dt) }}</span></div>
          <div class="field"><span class="f-label">Торги завершены</span><span class="f-val">{{ fmtDate(trip.context_end_dt) }}</span></div>
          <div class="field"><span class="f-label">Коэффициент</span><span class="f-val">{{ dash(trip.trip_factor) }}</span></div>
          <div class="field"><span class="f-label">Маршрут</span><span class="f-val">{{ dash(trip.city_out) }} → {{ dash(trip.city_in) }}</span></div>
          <div class="field"><span class="f-label">Загрузка</span><span class="f-val">{{ fmtDate(trip.departure_planed_dt) }}</span></div>
          <div class="field"><span class="f-label">Разгрузка</span><span class="f-val">{{ fmtDate(trip.destination_planed_dt) }}</span></div>
          <div class="field"><span class="f-label">Тип ТС / тоннаж</span><span class="f-val">{{ dash(trip.veh_type) }} / {{ dash(trip.veh_tonnage) }}</span></div>
          <div class="field"><span class="f-label">Вес груза, т</span><span class="f-val">{{ dash(trip.cargo_weight) }}</span></div>
          <div class="field"><span class="f-label">Груз</span><span class="f-val">{{ dash(trip.cargo_list) }}</span></div>
          <div class="field"><span class="f-label">Маршрут ATI</span><span class="f-val">{{ dash(trip.ati_route_id) }}</span></div>
          <div class="t360-flags">
            <flag-badge :value="trip.pb_task_active" label="Торги активны" />
            <flag-badge :value="trip.pb_has_bid" label="Были ставки" />
            <flag-badge :value="trip.pb_from_bot" label="Ставки делал бот" />
            <flag-badge :value="trip.win_is_bot" label="Выиграно ботом" />
            <flag-badge :value="trip.win_has_bid" label="Победа со ставкой" />
          </div>
        </div>

        <template v-else-if="activeTab === 'distribution'">
          <div class="t360-fields">
          <div class="field"><span class="f-label">Статус</span>
            <span class="f-val"><span class="t360-status-pill" :class="statusClass(trip.status_name)">{{ trip.status_name || '—' }}</span></span>
          </div>
          <div class="field cw-field">
            <span class="f-label">Закрытие</span>
            <span class="f-val">
              <div v-if="closeModel" class="cw-widget" :title="closeModel.tooltip">
                <div class="cw-track">
                  <div class="cw-bar" :class="closeModel.barCls" :style="{ width: closeModel.barPct + '%' }"></div>
                </div>
                <div v-if="closeModel.markPct != null" class="cw-mark" :style="{ left: closeModel.markPct + '%' }"></div>
                <div v-if="closeModel.mark12Pct != null" class="cw-mark cw-mark-12" :style="{ left: closeModel.mark12Pct + '%' }"></div>
              </div>
              <span v-else class="cw-empty">—</span>
            </span>
          </div>
          <div class="field"><span class="f-label">Платформа</span><span class="f-val">{{ dash(trip.display_name) }}</span></div>
          <div class="field"><span class="f-label">Сегмент</span><span class="f-val">{{ dash(trip.segment) }}</span></div>
          <div class="field"><span class="f-label">Менеджер</span><span class="f-val">{{ dash(trip.closer_name) }}</span></div>
          <div class="field"><span class="f-label">Логист</span><span class="f-val">{{ dash(trip.worker_name) }}</span></div>
          <div class="field"><span class="f-label">Поиск</span><span class="f-val">{{ fmtHours(trip.search_duration_sec) }}</span></div>
          <div class="field"><span class="f-label">В поиске</span><span class="f-val">{{ fmtHours(trip.ongoing_search_sec) }}</span></div>
          <div class="field"><span class="f-label">В работе всего</span><span class="f-val">{{ fmtHours(trip.total_time_in_work_sec) }}</span></div>
          <div class="field"><span class="f-label">Участников</span><span class="f-val">{{ dash(trip.participants_count) }}</span></div>
          <div class="field"><span class="f-label">Просрочено</span><span class="f-val">{{ dash(trip.expired_count) }}</span></div>
          <div class="field"><span class="f-label">Не на связи</span><span class="f-val">{{ dash(trip.unreachable_count) }}</span></div>
          <div class="field"><span class="f-label">Факт. стоимость</span><span class="f-val">{{ fmtMoney(trip.fact_cost_rub) }}</span></div>
          <div class="field"><span class="f-label">Тип закрытия</span><span class="f-val">{{ dash(trip.closing_type) }}</span></div>
          <div class="field"><span class="f-label">Тип исполнения</span><span class="f-val">{{ dash(trip.execution_type) }}</span></div>
          <div class="field"><span class="f-label">Карточка заведена</span><span class="f-val">{{ fmtDate(trip.card_create_dt) }}</span></div>
          <div class="field"><span class="f-label">Обновлена</span><span class="f-val">{{ fmtDate(trip.card_update_dt) }}</span></div>
          <div class="field"><span class="f-label">Закрыта</span><span class="f-val">{{ fmtDate(trip.closed_at) }}</span></div>
          <div class="t360-flags">
            <flag-badge :value="trip.is_overdue" label="Просрочен" />
          </div>
          </div>

          <div class="t360-history">
            <div class="t360-section-title">Хронология работы</div>
            <div v-if="historyLoading" class="t360-live-state"><my-loader /></div>
            <div v-else-if="historyError" class="t360-live-state t360-error">{{ historyError }}</div>
            <div v-else-if="!historyEvents.length" class="t360-live-state">События не фиксировались — лог ведётся с 31.08.2026</div>
            <ul v-else class="t360-history-feed">
              <li v-for="(ev, idx) in historyEvents" :key="idx" class="t360-history-row">
                <span class="h-time">{{ ev.time }}</span>
                <span class="h-src" :class="'src-' + ev.source">{{ ev.sourceLabel }}</span>
                <span class="h-text">{{ ev.text }}</span>
              </li>
            </ul>
          </div>
        </template>

        <div v-else-if="activeTab === 'executor'" class="t360-fields">
          <div v-if="!trip.has_erp_pair" class="t360-live-empty">Рейс не заведён в ERP — данных нет</div>
          <template v-else>
            <div v-if="executorLoading" class="t360-live-state"><my-loader /></div>
            <div v-else-if="executorError" class="t360-live-state t360-error">{{ executorError }}</div>
            <template v-else-if="executorTrip">
              <template v-if="executorTrip.executor">
                <div class="t360-section-title">Компания</div>
                <div class="field"><span class="f-label">Название</span><span class="f-val">{{ dash(executorTrip.executor.company?.name) }}</span></div>
                <div class="field"><span class="f-label">ИНН</span><span class="f-val">{{ dash(executorTrip.executor.company?.inn) }}</span></div>
                <div class="t360-section-title">Водитель</div>
                <div class="field"><span class="f-label">ФИО</span><span class="f-val">{{ dash(driverFullName(executorTrip.executor.driver)) }}</span></div>
                <div class="field"><span class="f-label">Телефон</span><span class="f-val">{{ dash(executorTrip.executor.driver?.phone) }}</span></div>
                <div class="field"><span class="f-label">Вод. удостоверение</span><span class="f-val">{{ dash(executorTrip.executor.driver?.license) }}</span></div>
                <div class="field"><span class="f-label">ИНН</span><span class="f-val">{{ dash(executorTrip.executor.driver?.inn || executorTrip.executor.driver?.generated_inn) }}</span></div>
                <div class="t360-section-title">Автомобиль</div>
                <div class="field"><span class="f-label">Марка</span><span class="f-val">{{ dash(brandName(executorTrip.executor.car)) }}</span></div>
                <div class="field"><span class="f-label">Номер</span><span class="f-val">{{ dash(executorTrip.executor.car?.number) }}</span></div>
                <div class="field"><span class="f-label">VIN</span><span class="f-val">{{ dash(executorTrip.executor.car?.vin) }}</span></div>
                <template v-if="executorTrip.executor.trailer">
                  <div class="t360-section-title">Прицеп</div>
                  <div class="field"><span class="f-label">Марка</span><span class="f-val">{{ dash(brandName(executorTrip.executor.trailer)) }}</span></div>
                  <div class="field"><span class="f-label">Номер</span><span class="f-val">{{ dash(executorTrip.executor.trailer?.number) }}</span></div>
                  <div class="field"><span class="f-label">VIN</span><span class="f-val">{{ dash(executorTrip.executor.trailer?.vin) }}</span></div>
                </template>
                <div class="t360-section-title">Менеджер</div>
                <div class="field"><span class="f-label">Имя</span><span class="f-val">{{ dash(managerName(executorTrip.executor.manager)) }}</span></div>
              </template>
              <div v-else class="t360-live-empty">Исполнитель не назначен</div>
              <template v-if="randomExecutor">
                <div class="t360-section-title">Случайный исполнитель</div>
                <div class="field"><span class="f-label">Водитель</span><span class="f-val">{{ dash(driverFullName(randomExecutor.driver)) }}</span></div>
                <div class="field"><span class="f-label">Автомобиль</span><span class="f-val">{{ dash(randomExecutor.car?.number) }}</span></div>
                <div v-if="randomExecutor.trailer" class="field"><span class="f-label">Прицеп</span><span class="f-val">{{ dash(randomExecutor.trailer?.number) }}</span></div>
                <div class="field"><span class="f-label">Статус</span><span class="f-val">{{ dash(randomExecutor.status) }}</span></div>
              </template>
            </template>
            <div v-else class="t360-live-empty">Нет данных</div>
          </template>
        </div>

        <div v-else-if="activeTab === 'downtime'" class="dt-pane">
          <div v-if="!trip.has_erp_pair" class="t360-live-empty">Рейс не заведён в ERP — данных нет</div>
          <template v-else>
            <div v-if="downtimeLoading" class="t360-live-state"><my-loader /></div>
            <div v-else-if="downtimeError" class="t360-live-state t360-error">{{ downtimeError }}</div>
            <template v-else-if="downtimeRows.length">
              <div v-if="downtimeScale" class="dt-scale-head">
                <span>{{ downtimeScale.from }}</span>
                <span class="dt-scale-hint">план → прибытие / сейчас · цвет — класс события</span>
                <span>{{ downtimeScale.to }}</span>
              </div>
              <table class="dt-table">
                <thead>
                  <tr>
                    <th>Операция</th>
                    <th>Склад</th>
                    <th>План</th>
                    <th>Прибытие</th>
                    <th class="num">Задержка</th>
                    <th class="num">Просрочка</th>
                    <th>Класс</th>
                    <th>График</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in downtimeRows" :key="row.route_point_id">
                    <td>{{ formatOperation(row) }}</td>
                    <td class="dt-warehouse" :title="row.warehouse_name">{{ dash(row.warehouse_name) }}</td>
                    <td class="num">{{ formatNaiveDate(row.planned_date) }}</td>
                    <td class="num">{{ formatNaiveDate(row.arrival_time) }}</td>
                    <td class="num">{{ formatMinutes(row.delay_minutes) }}</td>
                    <td class="num">{{ formatHours(row.overdue_hours) }}</td>
                    <td>
                      <span class="dt-tag" :style="classificationStyle(row.downtime_classification)">
                        {{ classificationMeta(row.downtime_classification).shortLabel }}
                      </span>
                    </td>
                    <td class="dt-chart-cell">
                      <div v-if="downtimeBar(row)" class="dt-chart" :title="downtimeBar(row).tooltip">
                        <div
                          class="dt-chart-bar"
                          :style="{ left: downtimeBar(row).left + '%', width: downtimeBar(row).width + '%', background: downtimeBar(row).color }"
                        ></div>
                      </div>
                      <span v-else>—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <div v-else class="t360-live-empty">Точек маршрута с плановыми датами нет — простоев не зафиксировано</div>
          </template>
        </div>

        <div v-else-if="activeTab === 'sync'" class="sync-pane">
          <div class="sync-services">
            <button
              v-for="svc in SYNC_SERVICES"
              :key="svc.id"
              type="button"
              class="sync-chip"
              :class="{ active: syncService === svc.id }"
              @click="syncService = svc.id"
            >{{ svc.label }}</button>
          </div>

          <div v-if="!canViewSync" class="t360-live-empty">Нет права «Журнал TMS» (tms.action_log) — данные синхронизации недоступны</div>
          <div v-else-if="!trip.has_erp_pair" class="t360-live-empty">Рейс не заведён в ERP — синхронизации с TMS нет</div>
          <template v-else>
            <div v-if="syncLoading" class="t360-live-state"><my-loader /></div>
            <div v-else-if="syncError" class="t360-live-state t360-error">{{ syncError }}</div>
            <template v-else>
              <template v-if="syncEntries.length">
                <div class="sync-section-title">Последние действия</div>
                <table class="sync-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Дата</th>
                      <th>Действие</th>
                      <th>Статус</th>
                      <th>Ошибка</th>
                      <th>Детали</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="row in syncEntries" :key="row.id">
                      <tr class="sync-row" :class="{ 'sync-row-open': syncExpanded.has(row.id) }" @click="toggleSyncRow(row.id)">
                        <td class="sync-caret">{{ syncExpanded.has(row.id) ? '▾' : '▸' }}</td>
                        <td>{{ fmtDate(row.created_dt) }}</td>
                        <td>{{ row.kind }}</td>
                        <td><span class="sync-status" :class="`sync-status-${row.status || 'info'}`">{{ row.status }}</span></td>
                        <td>{{ row.error_kind || '—' }}</td>
                        <td class="sync-detail" :title="row.error_detail || row.tms_message || ''">{{ row.error_detail || row.tms_message || '—' }}</td>
                      </tr>
                      <tr v-if="syncExpanded.has(row.id)" class="sync-row-detail">
                        <td colspan="6" class="sync-row-detail-td">
                          <div class="sync-detail-grid">
                            <div><span class="d-label">Поток</span><span>{{ row.flow || '—' }} · {{ row.direction || '—' }}</span></div>
                            <div><span class="d-label">Попытка</span><span>{{ row.attempt_no ?? '—' }}</span></div>
                            <div><span class="d-label">Актор</span><span>{{ row.actor || '—' }}</span></div>
                            <div><span class="d-label">Источник</span><span>{{ row.source_service || '—' }}</span></div>
                            <div><span class="d-label">tms_id</span><span>{{ row.tms_id || '—' }}</span></div>
                            <div><span class="d-label">cargo_trip_id</span><span>{{ row.cargo_trip_id ?? '—' }}</span></div>
                          </div>
                          <pre v-if="row.error_detail" class="sync-pre">{{ row.error_detail }}</pre>
                          <pre v-if="row.tms_message" class="sync-pre sync-pre-muted">TMS: {{ row.tms_message }}</pre>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </template>
              <div v-else class="t360-live-empty">Записей синхронизации по рейсу нет</div>

              <router-link class="sync-log-link" :to="syncLogLink">Открыть журнал TMS →</router-link>
            </template>
          </template>
        </div>

        <div v-else class="t360-fields">
          <div class="field"><span class="f-label">Рейс в ERP</span><span class="f-val">{{ dash(trip.erp_trip_id) }}</span></div>
          <div class="field"><span class="f-label">Заказчик</span><span class="f-val">{{ dash(trip.customer_name) }}</span></div>
          <div class="field"><span class="f-label">Ставка заказчика, ₽</span><span class="f-val">{{ fmtMoney(trip.customer_gross) }}</span></div>
          <div class="field"><span class="f-label">Ставка перевозчика, ₽</span><span class="f-val">{{ fmtMoney(trip.carrier_price_eff) }}</span></div>
          <div class="field"><span class="f-label">Маржа</span><span class="f-val">{{ fmtPct(trip.margin_pct) }}</span></div>
          <div class="field"><span class="f-label">Рентабельность</span><span class="f-val">{{ fmtPct(trip.profitability_pct) }}</span></div>
          <div class="field"><span class="f-label">Ожидаемая комиссия</span><span class="f-val">{{ fmtMoney(trip.commission_rub) }}</span></div>
          <div class="field"><span class="f-label">В Фонд Кипиай / из фонда, ₽</span><span class="f-val">{{ fmtMoney(trip.kpi_fund_put_rub) }} / {{ fmtMoney(trip.kpi_fund_take_rub) }}</span></div>
          <div class="field"><span class="f-label">Чистая комиссия</span><span class="f-val">{{ fmtMoney(trip.net_commission_rub) }}</span></div>
          <div class="field"><span class="f-label">Штрафы за срывы</span><span class="f-val">{{ fmtMoney(trip.breakdown_loss_rub) }}</span></div>
          <div class="field"><span class="f-label">Финрезультат</span><span class="f-val">{{ fmtMoney(trip.financial_result_rub) }}</span></div>
          <div class="field"><span class="f-label">Ответственный</span><span class="f-val">{{ dash(trip.responsible_name) }}</span></div>
          <div class="field"><span class="f-label">Статус в ERP</span><span class="f-val">{{ dash(trip.work_status_name) }}</span></div>
          <div class="field"><span class="f-label">Тип клиента</span><span class="f-val">{{ dash(trip.client_type) }}</span></div>
          <div class="field"><span class="f-label">Спот</span><span class="f-val">{{ dash(trip.spot_type) }}</span></div>
          <div class="field"><span class="f-label">Перевозчик (TMS)</span><span class="f-val">{{ dash(trip.tms_carrier_name) }}</span></div>
          <div class="field"><span class="f-label">Маршрут (TMS)</span><span class="f-val">{{ dash(trip.tms_city_out) }} → {{ dash(trip.tms_city_in) }}</span></div>
          <div class="field"><span class="f-label">Период (TMS)</span><span class="f-val">{{ fmtDate(trip.tms_date_start) }} — {{ fmtDate(trip.tms_date_end) }}</span></div>
          <div class="field"><span class="f-label">Создан в ERP</span><span class="f-val">{{ fmtDate(trip.erp_created_dt) }}</span></div>
          <div class="t360-flags">
            <flag-badge :value="trip.is_carousel" label="Карусель" />
            <flag-badge :value="trip.has_erp_pair" label="Заведён в ERP" />
            <flag-badge :value="trip.increased_tariff" label="Повышенный тариф" />
          </div>
        </div>
      </div>
    </template>
  </template>
</template>

<script>
import { ref, computed, watch, defineComponent, h } from 'vue'
import { useStore } from 'vuex'
import { createToaster } from '@meforma/vue-toaster'
import { MODE_COMPACT, trip360StatusClass } from '@/components/trip360/trip360Logic.mjs'
import { formatHistoryEvent } from '@/components/trip360/trip360Logic.mjs'
import { fetchTrip360History } from '@/backend/trip360Api'
import { fmtDate, fmtHours, fmtMoney, fmtPct } from '@/pages/statistics/format'
import { closeTimelineModel } from '@/pages/statistics/closeTimeline'
import authHeader from '@/backend/auth/auth-header'
import { useAxiosCreate } from '@/backend/utils'
import { fetchDowntimes } from '@/backend/downtimesApi'
import { fetchTmsActionLog } from '@/backend/tmsActionLogApi'
import { pricingLookup } from '@/backend/atiPriceCheckApi'
import { humanizeApiError } from '@/backend/errorMessages'
import { lookupBaseRate } from '@/backend/baseRatesApi'
import { fetchPureOrder } from '@/backend/pureBiddingApi'
import AdviseRules from '@/components/advise/AdviseRules.vue'
import {
  classificationMeta,
  formatHours,
  formatMinutes,
  formatNaiveDate,
  formatOperation,
  parseNaiveDateParts,
} from '@/pages/downtimes/downtimeOptions'

const FlagBadge = defineComponent({
  name: 'FlagBadge',
  props: { value: { type: Boolean, default: null }, label: { type: String, required: true } },
  setup(props) {
    return () =>
      props.value === true
        ? h('span', { class: 'badge badge-on' }, props.label)
        : props.value === false
          ? h('span', { class: 'badge badge-off' }, props.label)
          : h('span', { class: 'badge badge-gray' }, props.label)
  },
})

const TABS = [
  { id: 'summary', label: 'Сводка' },
  { id: 'finance', label: 'Финансы' },
  { id: 'timeline', label: 'Таймлайн' },
  { id: 'route', label: 'Маршрут' },
  { id: 'bidding', label: 'Торги' },
  { id: 'distribution', label: 'Распределение' },
  { id: 'erp', label: 'Рейсы' },
  { id: 'executor', label: 'Исполнитель' },
  { id: 'downtime', label: 'Простой' },
  { id: 'sync', label: 'Синхронизация' },
]

// Вкладка «Синхронизация»: внешние сервисы, куда уходит рейс.
// Пока единственный — TMS; список расширяется по мере появления интеграций.
const SYNC_SERVICES = [
  { id: 'tms', label: 'TMS' },
]

// Живые данные рейса ERP (исполнитель): GET /api/v2/trips/{pk}.
const tripsApi = useAxiosCreate('trips')
function fetchExecutorTrip(tripPk) {
  return tripsApi.get(String(tripPk), { headers: authHeader() }).then((response) => response.data)
}

// Цвет мини-графика точки на шкале простоев — по классу события.
const DT_BAR_COLORS = {
  confirmed: '#f04438',
  potential: '#f79009',
  late: '#3b82f6',
  upcoming: '#0e9388',
  resolved: '#12b76a',
  incomplete: '#98a2b3',
  none: '#d0d5dd',
}

// Плитки вкладки «Финансы»: порядок и подписи как в directMetrics калькулятора
// (CommercialCalculator.vue), после них — служебные метрики снапшота.
const FINANCE_METRIC_DEFS = [
  { label: 'Тариф заказчика с НДС', key: 'customer_gross', kind: 'money' },
  { label: 'Тариф заказчика без НДС', key: 'customer_net', kind: 'money' },
  { label: 'Резерв прибыли', key: 'profit_reserve', kind: 'money' },
  { label: 'Ставка перевозчика с НДС', key: 'carrier_price_eff', kind: 'money', withSource: true },
  { label: 'Ставка перевозчика без НДС', key: 'carrier_price_net', kind: 'money' },
  { label: 'Налоговый фонд', key: 'tax_fund', kind: 'money' },
  { label: 'АВТОЛИДЕР', key: 'autoleader', kind: 'money', highlight: true },
  { label: 'KPI', key: 'kpi_amount', kind: 'money', highlight: true, rateKey: 'kpi_rate' },
  { label: 'ЗП менеджера', key: 'manager_salary_rub', kind: 'money', highlight: true, rateKey: 'manager_salary_rate', rateInBrackets: true },
  { label: 'Прибыльность', key: 'profitability_pct', kind: 'pct' },
  { label: 'Остаток компании', key: 'company_remainder_rub', kind: 'money' },
  { label: 'Ожидаемая комиссия', key: 'commission_rub', kind: 'money' },
  { label: 'Разница тарифов', key: 'tariff_difference', kind: 'money' },
  { label: 'Наценка', key: 'markup_pct', kind: 'pct' },
  { label: 'Маржа', key: 'margin_pct', kind: 'pct' },
  { label: 'НДС-разница', key: 'vat_gap', kind: 'money' },
  { label: 'База KPI', key: 'kpi_base', kind: 'money' },
  { label: 'Факт (карточка)', key: 'fact_cost_rub', kind: 'money' },
  { label: 'Комиссия чистая (после убытков)', key: 'net_commission_rub', kind: 'money' },
  { label: 'Убыток (срыв)', key: 'breakdown_loss_rub', kind: 'money' },
]

// Как copyNumber в калькуляторе: число с двумя знаками, null если копировать нечего.
function finCopyNumber(value) {
  if (value === null || value === undefined || value !== value) return null
  const num = Number(value)
  return Number.isFinite(num) ? num.toFixed(2) : null
}

const TIMELINE_COLORS = {
  auction_created: '#5925dc',
  auction_context: '#5925dc',
  card_created: '#1d4ed8',
  worker_joined: '#98a2b3',
  worker_left: '#667085',
  card_closed: '#027a48',
  card_canceled: '#98a2b3',
  card_breakdown: '#b42318',
  card_refusal: '#b42318',
  erp_created: '#12b76a',
  planned_load: '#667085',
  planned_point: '#175cd3',
}

const TIMELINE_LEGEND = [
  { label: 'Заявка на торгах', color: '#5925dc', kinds: ['auction_created', 'auction_context'] },
  { label: 'Карточка распределения', color: '#1d4ed8', kinds: ['card_created'] },
  { label: 'В работе', color: '#98a2b3', kinds: ['worker_joined', 'worker_left'] },
  { label: 'Закрыт / отменён', color: '#027a48', kinds: ['card_closed', 'card_canceled'] },
  { label: 'Срыв / отказ', color: '#b42318', kinds: ['card_breakdown', 'card_refusal'] },
  { label: 'Рейс в ERP', color: '#12b76a', kinds: ['erp_created'] },
  { label: 'Точки маршрута (план)', color: '#175cd3', kinds: ['planned_point'] },
  { label: 'Плановая погрузка', color: '#667085', kinds: ['planned_load'] },
]

export default {
  name: 'Trip360Content',
  components: { FlagBadge, AdviseRules },
  props: {
    trip: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    mode: { type: String, default: MODE_COMPACT },
    initialTab: { type: String, default: null },
  },
  setup(props) {
    const toaster = createToaster()
    const store = useStore()
    const activeTab = ref(props.initialTab || 'summary')

    // Повторное открытие окна с параметром вкладки.
    watch(
      () => props.initialTab,
      (tab) => {
        const next = tab || 'summary'
        if (activeTab.value !== next) activeTab.value = next
      },
    )

    // Дата завершения — первое из заполненных событий закрытия карточки.
    const endedAt = computed(() => {
      const t = props.trip
      if (!t) return null
      return t.closed_at || t.canceled_at || t.breakdown_at || t.refusal_at || null
    })

    async function copyVal(value) {
      if (value === null || value === undefined || value === '') return
      try {
        await navigator.clipboard.writeText(String(value))
        toaster.info(`Скопировано: ${value}`)
      } catch {
        toaster.error('Не удалось скопировать в буфер обмена')
      }
    }

    const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value)
    const SOURCE_LABELS = { fact: 'факт', approved: 'соглас.', manual: 'ручной', base: 'база' }
    const sourceLabel = (source) => SOURCE_LABELS[source] || source
    const moneyRange = (from, to) => {
      const a = fmtMoney(from)
      const b = fmtMoney(to)
      return a === b ? a : `${a} → ${b}`
    }
    const statusClass = trip360StatusClass

    // Плитки «Финансы»: значение + копируемое число (как copyNumber калькулятора).
    const financeMetrics = computed(() => {
      const t = props.trip || {}
      return FINANCE_METRIC_DEFS.map((def) => {
        const raw = t[def.key]
        let label = def.label
        if (def.rateKey) {
          const rate = t[def.rateKey]
          if (rate !== null && rate !== undefined && rate === rate) {
            label = def.rateInBrackets
              ? `${def.label} (${fmtPct(rate * 100)})`
              : `${def.label} · ${fmtPct(rate * 100)}`
          }
        }
        const copyValue = finCopyNumber(raw)
        return {
          label,
          value: def.kind === 'pct' ? fmtPct(raw) : fmtMoney(raw),
          copyValue,
          title: copyValue === null ? def.label : `Скопировать: ${copyValue}`,
          highlight: Boolean(def.highlight),
          sub: def.withSource && t.carrier_price_source
            ? sourceLabel(t.carrier_price_source)
            : null,
        }
      })
    })

    // Таймлайн: события из снапшота (timeline_json) + рейс в ERP из erp_created_dt.
    const parseTs = (v) => {
      if (v === null || v === undefined || v === '') return null
      if (typeof v === 'number') return new Date(v > 1e11 ? v : v * 1000)
      const d = new Date(v)
      return Number.isNaN(d.getTime()) ? null : d
    }

    const timelineEvents = computed(() => {
      const t = props.trip
      if (!t) return []
      let events = []
      try {
        const parsed = t.timeline_json ? JSON.parse(t.timeline_json) : []
        events = Array.isArray(parsed) ? parsed.filter((e) => e && e.ts) : []
      } catch {
        events = []
      }
      const erpTs = parseTs(t.erp_created_dt)
      if (erpTs) {
        events = events.concat([{ ts: erpTs.toISOString(), kind: 'erp_created', label: 'Рейс заведён в ERP' }])
      }
      // маршрутные точки рейса (route_points_json из downtimes-снапшота):
      // плановые погрузки/разгрузки с городом — расширяют шкалу до финальной точки
      try {
        const points = t.route_points_json ? JSON.parse(t.route_points_json) : []
        if (Array.isArray(points)) {
          points.forEach((p) => {
            const ts = parseTs(p && p.planned_date)
            if (!ts) return
            const city = p.is_loading ? p.city_out : p.city_in
            const label = `${p.is_loading ? 'Погрузка' : 'Разгрузка'}${city ? ' · ' + city : ''}`
            events = events.concat([{
              ts: ts.toISOString(),
              kind: 'planned_point',
              label,
              city: city || null,
              warehouse: p.warehouse_name || null,
              operation: p.operation_type || null,
            }])
          })
        }
      } catch {
        /* повреждённый JSON маршрутных точек — игнорируем */
      }
      const sorted = events
        .map((e) => ({ ...e, _ts: parseTs(e.ts) }))
        .filter((e) => e._ts)
        .sort((a, b) => a._ts.getTime() - b._ts.getTime())
      if (!sorted.length) return []
      const t0 = sorted[0]._ts.getTime()
      const t1 = sorted[sorted.length - 1]._ts.getTime()
      const span = Math.max(t1 - t0, 3600000)
      return sorted.map((e) => ({
        ...e,
        _pct: Math.min(100, Math.max(0, ((e._ts.getTime() - t0) / span) * 100)),
      }))
    })

    const hoveredIdx = ref(-1)

    const timelineSegments = computed(() => {
      const events = timelineEvents.value
      const segments = []
      for (let i = 0; i < events.length - 1; i += 1) {
        const left = events[i]._pct
        const right = events[i + 1]._pct
        const width = right - left
        if (width <= 0) continue
        segments.push({
          left,
          width,
          color: timelineColor(events[i]),
          label: `${events[i].label}: ${fmtTs(events[i].ts)} — ${fmtTs(events[i + 1].ts)}`,
        })
      }
      return segments
    })

    const timelineColor = (event) => TIMELINE_COLORS[event.kind] || '#98a2b3'

    const timelineLegend = computed(() => {
      const present = new Set(timelineEvents.value.map((e) => e.kind))
      return TIMELINE_LEGEND.filter((g) => g.kinds.some((k) => present.has(k)))
    })

    const fmtTs = (ts) => {
      const d = parseTs(ts)
      if (!d) return '—'
      return d.toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    }

    const gapLabel = (prev, next) => {
      const diffMs = next._ts.getTime() - prev._ts.getTime()
      if (diffMs <= 0) return '0'
      const minutes = Math.round(diffMs / 60000)
      if (minutes < 60) return `${minutes} мин`
      const hours = minutes / 60
      if (hours < 48) return `${hours.toFixed(hours < 10 ? 1 : 0)} ч`
      const days = hours / 24
      return `${days.toFixed(days < 10 ? 1 : 0)} дн`
    }

    const eventMeta = (event) => {
      const parts = []
      if (event.user) parts.push(event.user)
      if (event.reason) parts.push(`причина: ${event.reason}`)
      if (event.state) parts.push(`из «${event.state}»`)
      if (event.duration_sec !== null && event.duration_sec !== undefined && !Number.isNaN(Number(event.duration_sec))) {
        parts.push(`в работе: ${fmtHours(event.duration_sec)}`)
      }
      if (event.warehouse) parts.push(event.warehouse)
      if (event.operation) parts.push(event.operation)
      return parts.join(' · ')
    }

    // ── Виджет «Закрытие» (дубль колонки из таблицы рейсов статистики) ──
    const closeModel = computed(() => (props.trip ? closeTimelineModel(props.trip) : null))

    // ── Live-вкладки: «Исполнитель» и «Простой» (живые эндпоинты, lazy) ──
    const executorTrip = ref(null)
    const executorLoading = ref(false)
    const executorError = ref(null)
    let executorSeq = 0

    const downtimeRows = ref([])
    const downtimeLoading = ref(false)
    const downtimeError = ref(null)
    let downtimeSeq = 0

    async function loadExecutor() {
      const pk = Number(props.trip?.erp_trip_pk)
      if (!props.trip?.has_erp_pair || !Number.isFinite(pk) || pk <= 0 || executorLoading.value) return
      const seq = ++executorSeq
      executorLoading.value = true
      executorError.value = null
      try {
        const data = await fetchExecutorTrip(pk)
        if (seq !== executorSeq) return
        executorTrip.value = data && typeof data === 'object' ? data : null
      } catch {
        if (seq !== executorSeq) return
        executorError.value = 'Не удалось загрузить данные исполнителя'
        executorTrip.value = null
      } finally {
        if (seq === executorSeq) executorLoading.value = false
      }
    }

    async function loadDowntimes() {
      const key = props.trip?.erp_trip_id
      if (!props.trip?.has_erp_pair || !key || downtimeLoading.value) return
      const seq = ++downtimeSeq
      downtimeLoading.value = true
      downtimeError.value = null
      try {
        const data = await fetchDowntimes({ trip_id_prefix: key, status: 'all', page: 1 })
        if (seq !== downtimeSeq) return
        downtimeRows.value = Array.isArray(data?.items) ? data.items : []
      } catch {
        if (seq !== downtimeSeq) return
        downtimeError.value = 'Не удалось загрузить простои'
        downtimeRows.value = []
      } finally {
        if (seq === downtimeSeq) downtimeLoading.value = false
      }
    }

    // Вкладка «Синхронизация»: сводка по интеграциям рейса (пока только TMS).
    const syncService = ref('tms')
    const canViewSync = computed(() => Boolean(store.getters.hasPermission('tms.action_log')))
    const syncLoading = ref(false)
    const syncError = ref(null)
    const syncEntries = ref([])
    const syncExpanded = ref(new Set())
    let syncSeq = 0

    function toggleSyncRow(id) {
      const next = new Set(syncExpanded.value)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      syncExpanded.value = next
    }

    async function loadSync() {
      const key = props.trip?.erp_trip_id
      if (syncLoading.value || !canViewSync.value || !props.trip?.has_erp_pair || !key) return
      const seq = ++syncSeq
      syncLoading.value = true
      syncError.value = null
      try {
        const logPage = await fetchTmsActionLog({ trip_id: key, per_page: 10 })
        if (seq !== syncSeq) return
        syncEntries.value = Array.isArray(logPage?.items) ? logPage.items : []
      } catch {
        if (seq !== syncSeq) return
        syncError.value = 'Не удалось загрузить данные синхронизации TMS'
        syncEntries.value = []
      } finally {
        if (seq === syncSeq) syncLoading.value = false
      }
    }

    const syncLogLink = computed(() => `/admin/tms-action-log?trip_id=${encodeURIComponent(props.trip?.erp_trip_id || '')}`)

    // ── «Хронология работы» (таб Распределение): живой лог distribution_status_history ──
    const historyEvents = ref([])
    const historyLoading = ref(false)
    const historyError = ref(null)
    let historySeq = 0

    async function loadHistory() {
      const key = props.trip?.distr_id || props.trip?.uid
      if (!key || historyLoading.value) return
      const seq = ++historySeq
      historyLoading.value = true
      historyError.value = null
      try {
        const raw = await fetchTrip360History(String(key))
        if (seq !== historySeq) return
        historyEvents.value = (raw || []).map((e) => formatHistoryEvent(e))
      } catch {
        if (seq !== historySeq) return
        historyError.value = 'Не удалось загрузить хронологию работы'
        historyEvents.value = []
      } finally {
        if (seq === historySeq) historyLoading.value = false
      }
    }

    // T2: загрузка при первом заходе + авто-обновление при повторном заходе.
    watch(activeTab, (tab) => {
      if (tab === 'executor') loadExecutor()
      else if (tab === 'downtime') loadDowntimes()
      else if (tab === 'route') loadRouteData()
      else if (tab === 'sync') loadSync()
      else if (tab === 'distribution') loadHistory()
    })

    // ⟳ в строке табов — только на live-вкладках (Маршрут / Распределение / Исполнитель / Простой / Синхронизация).
    const isLiveTab = computed(
      () => activeTab.value === 'route'
        || activeTab.value === 'distribution'
        || ((activeTab.value === 'executor' || activeTab.value === 'downtime' || activeTab.value === 'sync')
          && !!props.trip?.has_erp_pair),
    )
    const liveTabLoading = computed(() => {
      if (activeTab.value === 'route') return routeLoading.value
      if (activeTab.value === 'executor') return executorLoading.value
      if (activeTab.value === 'sync') return syncLoading.value
      if (activeTab.value === 'distribution') return historyLoading.value
      return downtimeLoading.value
    })
    function reloadLiveTab() {
      if (activeTab.value === 'route') loadRouteData()
      else if (activeTab.value === 'executor') loadExecutor()
      else if (activeTab.value === 'downtime') loadDowntimes()
      else if (activeTab.value === 'sync') loadSync()
      else if (activeTab.value === 'distribution') loadHistory()
    }

    // Смена карточки — сбрасываем live-данные всех вкладок.
    watch(
      () => (props.trip ? `${props.trip.uid || ''}|${props.trip.distr_id || ''}` : ''),
      () => {
        executorSeq += 1
        downtimeSeq += 1
        routeSeq += 1
        syncSeq += 1
        historySeq += 1
        executorTrip.value = null
        executorLoading.value = false
        executorError.value = null
        downtimeRows.value = []
        downtimeLoading.value = false
        downtimeError.value = null
        syncEntries.value = []
        syncExpanded.value = new Set()
        syncLoading.value = false
        syncError.value = null
        historyEvents.value = []
        historyLoading.value = false
        historyError.value = null
        priceLookup.value = null
        priceLookupError.value = null
        baseRate.value = null
        baseRateForbidden.value = false
        pureOrder.value = null
        pureOrderError.value = null
        routeLoading.value = false
        routeTransport.value = defaultTransport(props.trip?.veh_type)
        if (activeTab.value === 'executor') loadExecutor()
        else if (activeTab.value === 'downtime') loadDowntimes()
        else if (activeTab.value === 'route') loadRouteData()
        else if (activeTab.value === 'sync') loadSync()
        else if (activeTab.value === 'distribution') loadHistory()
      },
    )

    const randomExecutor = computed(() => executorTrip.value?.random_executor || null)

    const driverFullName = (driver) => {
      if (!driver) return null
      return [driver.last_name, driver.first_name, driver.surname].filter(Boolean).join(' ') || null
    }
    const brandName = (item) => item?.security_info?.[0]?.avtocod_identifiers_brand_name || null
    const managerName = (manager) => {
      if (!manager) return null
      return manager.name || manager.full_name || [manager.last_name, manager.first_name].filter(Boolean).join(' ') || null
    }

    // Naive-даты простоев ('2026-08-24 10:00') — локальное время.
    function naiveTs(value) {
      const parts = parseNaiveDateParts(value)
      if (parts) {
        const d = new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute))
        return Number.isNaN(d.getTime()) ? null : d.getTime()
      }
      const parsed = parseTs(value)
      return parsed ? parsed.getTime() : null
    }

    // Общая шкала простоев: min(план) → max(прибытие/убытие, сейчас).
    const downtimeScale = computed(() => {
      const rows = downtimeRows.value
      if (!rows.length) return null
      const now = Date.now()
      const starts = rows.map((r) => naiveTs(r.planned_date)).filter((t) => t != null)
      if (!starts.length) return null
      const ends = rows.map((r) => naiveTs(r.arrival_time) || naiveTs(r.departure_time)).filter((t) => t != null)
      const t0 = Math.min(...starts)
      const t1 = Math.max(...ends, now, t0 + 60 * 60 * 1000)
      const fmt = (ts) => new Date(ts).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
      return { t0, t1, from: fmt(t0), to: fmt(t1) }
    })

    const downtimeBarMap = computed(() => {
      const scale = downtimeScale.value
      const map = new Map()
      if (!scale) return map
      const span = scale.t1 - scale.t0
      const pct = (t) => Math.min(100, Math.max(0, ((t - scale.t0) / span) * 100))
      const now = Date.now()
      downtimeRows.value.forEach((row) => {
        const start = naiveTs(row.planned_date)
        if (start == null) return
        const end = naiveTs(row.arrival_time) || naiveTs(row.departure_time) || now
        const left = pct(start)
        const meta = classificationMeta(row.downtime_classification)
        map.set(row.route_point_id, {
          left,
          width: Math.max(pct(end) - left, 0),
          color: DT_BAR_COLORS[meta.value] || DT_BAR_COLORS.none,
          tooltip: `План: ${formatNaiveDate(row.planned_date)}\nПрибытие: ${formatNaiveDate(row.arrival_time)}\nКласс: ${meta.label}`,
        })
      })
      return map
    })
    const downtimeBar = (row) => downtimeBarMap.value.get(row.route_point_id) || null

    const classificationStyle = (value) => {
      const meta = classificationMeta(value)
      return { color: meta.color, background: meta.background }
    }

    // ── Вкладка «Маршрут»: расчёт ATI + правила бота + точки ──
    const routeLoading = ref(false)
    const priceLookup = ref(null)
    const priceLookupError = ref(null)
    const baseRate = ref(null)
    const baseRateForbidden = ref(false)
    const routeTransport = ref(defaultTransport(props.trip?.veh_type))
    const pureOrder = ref(null)
    const pureOrderError = ref(null)
    let routeSeq = 0

    const hasCities = computed(() => !!(props.trip?.city_out && props.trip?.city_in))

    function defaultTransport(vehType) {
      const v = String(vehType || '').toLowerCase()
      if (/реф|ref/.test(v)) return 'ref'
      if (/изотерм|iso/.test(v)) return 'iso'
      return 'tent'
    }

    async function loadRouteData() {
      const t = props.trip
      if (!t || routeLoading.value) return
      const seq = ++routeSeq
      routeLoading.value = true
      if (hasCities.value) {
        try {
          const data = await pricingLookup({
            departure_city: String(t.city_out).trim(),
            destination_city: String(t.city_in).trim(),
            transport_type: null,
          })
          if (seq !== routeSeq) return
          priceLookup.value = data || null
          priceLookupError.value = null
        } catch (err) {
          if (seq !== routeSeq) return
          priceLookup.value = null
          const reason = humanizeApiError(err)
          priceLookupError.value = `Не удалось рассчитать стоимость направления: ${reason}`
        }
        try {
          baseRateForbidden.value = false
          const data = await lookupBaseRate({ from_city: String(t.city_out).trim(), to_city: String(t.city_in).trim() })
          if (seq !== routeSeq) return
          baseRate.value = data || null
        } catch (err) {
          if (seq !== routeSeq) return
          baseRate.value = null
          if (err?.response?.status === 403) baseRateForbidden.value = true
        }
      }
      const pureUid = t.pb_uid || t.uid
      if (pureUid) {
        try {
          const data = await fetchPureOrder(pureUid)
          if (seq !== routeSeq) return
          pureOrder.value = data || null
          pureOrderError.value = null
        } catch (err) {
          if (seq !== routeSeq) return
          pureOrder.value = null
          pureOrderError.value = err?.response?.status === 404 ? null : 'Не удалось загрузить торги'
        }
      }
      if (seq === routeSeq) routeLoading.value = false
      if (t.has_erp_pair) loadExecutor()
    }

    const VAT_RATE = 0.22
    function normalizePriceEntry(entry) {
      if (!entry) return null
      const out = { ...entry }
      const hasWith = entry.avg_volume_with_nds != null
      const hasWithout = entry.avg_volume_without_nds != null
      if (hasWith && !hasWithout) out.realVariant = 'with'
      else if (hasWithout && !hasWith) out.realVariant = 'without'
      else out.realVariant = 'both'
      ;(['min', 'avg', 'max']).forEach((base) => {
        const wKey = `${base}_volume_with_nds`
        const woKey = `${base}_volume_without_nds`
        const w = out[wKey]
        const wo = out[woKey]
        if (w != null && wo == null) out[woKey] = Math.round(w / (1 + VAT_RATE))
        else if (wo != null && w == null) out[wKey] = Math.round(wo * (1 + VAT_RATE))
      })
      return out
    }

    const TRANSPORT_CHIPS = [
      { key: 'tent', label: 'Тент' },
      { key: 'ref', label: 'Реф' },
      { key: 'iso', label: 'ISO' },
    ]

    const fmtPrice = (v) => (v == null ? '—' : new Intl.NumberFormat('ru-RU').format(v))
    const fmtRouteDate = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return String(iso)
      return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    }

    const featuredEntry = computed(() => normalizePriceEntry(priceLookup.value?.prices?.[routeTransport.value] || null))
    const featuredPrice = computed(() => featuredEntry.value?.avg_volume_with_nds ?? null)
    const featuredWithoutNds = computed(() => featuredEntry.value?.avg_volume_without_nds ?? null)
    const featuredDerived = computed(() => !!featuredEntry.value?.is_derived)
    const featuredLabel = computed(() => {
      const chip = TRANSPORT_CHIPS.find((x) => x.key === routeTransport.value)
      return chip ? chip.label : routeTransport.value
    })
    const priceCells = computed(() => TRANSPORT_CHIPS.map((chip) => ({ ...chip, entry: normalizePriceEntry(priceLookup.value?.prices?.[chip.key] || null) })))
    const totalCost = computed(() => {
      const rate = featuredEntry.value?.avg_volume_with_nds
      const dist = priceLookup.value?.distance
      if (rate == null || !dist) return null
      return Math.round(rate * dist)
    })
    const totalCostWithoutNds = computed(() => {
      const rate = featuredEntry.value?.avg_volume_without_nds
      const dist = priceLookup.value?.distance
      if (rate == null || !dist) return null
      return Math.round(rate * dist)
    })
    const baseMargin = computed(() => {
      const base = baseRate.value?.cost_with_nds
      const total = totalCost.value
      if (!base || total == null) return null
      return Math.round(((base - total) / base) * 100)
    })
    const routeMismatch = computed(() => {
      const lookupId = priceLookup.value?.ati_route_id
      const tripId = props.trip?.ati_route_id
      return !!(lookupId && tripId && Number(lookupId) !== Number(tripId))
    })
    const refreshStatusLabel = computed(() => {
      const s = priceLookup.value?.last_refresh?.status
      const map = {
        success: 'успешно',
        not_found: 'не найдено',
        budget_exhausted: 'бюджет исчерпан',
        transient_error: 'временная ошибка',
        error: 'ошибка',
        priority_route: 'маршрут в приоритете',
      }
      return map[s] || s || ''
    })
    const canYandexLink = computed(() => !!priceLookup.value?.departure_geo_point && !!priceLookup.value?.destination_geo_point)
    const yandexLink = computed(() => {
      const r = priceLookup.value
      if (!r) return '#'
      return `https://yandex.ru/maps/?rtext=${r.departure_geo_point}~${r.destination_geo_point}&rtt=auto`
    })
    const botTask = computed(() => pureOrder.value?.context?.task || null)
    const pureOrderAdvise = computed(() => (Array.isArray(pureOrder.value?.context?.advise) ? pureOrder.value.context.advise : []))

    const routePointsModel = computed(() => {
      const t = props.trip
      if (!t) return []
      let snap = []
      try {
        const parsed = t.route_points_json ? JSON.parse(t.route_points_json) : []
        snap = Array.isArray(parsed) ? parsed : []
      } catch {
        snap = []
      }
      const live = Array.isArray(executorTrip.value?.route_point) ? executorTrip.value.route_point : null
      const snapById = new Map(snap.filter((p) => p && p.route_point_id != null).map((p) => [String(p.route_point_id), p]))
      const fmtDt = (v) => {
        const ts = naiveTs(v)
        if (ts == null) return null
        return new Date(ts).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
      }
      const build = (src) => {
        const s = src.snap || {}
        const l = src.live || null
        const operation = l?.operation_type || s.operation_type || null
        const is_loading = typeof s.is_loading === 'boolean' ? s.is_loading : (operation ? !/выгруз/i.test(String(operation)) : true)
        const city = is_loading ? s.city_out : s.city_in
        const planned = l ? (l.correct_planned_datetime || l.planned_date) : s.planned_date
        const arrival = l?.arrival_time || null
        let delayMin = null
        const plannedTs = naiveTs(planned)
        const arrivalTs = naiveTs(arrival)
        if (plannedTs != null && arrivalTs != null) delayMin = Math.round((arrivalTs - plannedTs) / 60000)
        const belated = l ? l.belated === true : false
        const late = belated || (delayMin != null && delayMin >= 60)
        const delayLabel = delayMin != null && delayMin > 0
          ? (delayMin >= 60 ? `Опоздание ${Math.floor(delayMin / 60)} ч ${delayMin % 60} мин` : `Опоздание ${delayMin} мин`)
          : 'Опоздание'
        const contacts = [l?.sender_info, l?.loading_contact, l?.unloading_contact].filter(Boolean).join(' · ') || null
        const factParts = []
        if (arrival) factParts.push({ label: 'Прибытие', value: fmtDt(arrival) })
        const opStart = l ? (l.actual_start_operation_time ?? l.actual_start_operationTime) : null
        const opEnd = l ? (l.actual_end_operation_time ?? l.actual_end_operationTime) : null
        if (opStart || opEnd) factParts.push({ label: 'Операции', value: `${fmtDt(opStart) || '—'} — ${fmtDt(opEnd) || '—'}` })
        if (l?.departure_time) factParts.push({ label: 'Убытие', value: fmtDt(l.departure_time) })
        return {
          key: String(l?.id ?? s.route_point_id ?? `${is_loading}-${factParts.length}-${Math.random()}`),
          is_loading,
          city: city || null,
          warehouse: l?.warehouse_name || s.warehouse_name || null,
          address: l?.address || null,
          plannedLabel: fmtDt(planned),
          late,
          delayLabel,
          contacts,
          factParts,
        }
      }
      if (live && live.length) return live.map((l) => build({ live: l, snap: snapById.get(String(l.id)) || null }))
      return snap.map((s) => build({ snap: s }))
    })

    const latePointsCount = computed(() => routePointsModel.value.filter((p) => p.late).length)
    const pointsLiveLoading = computed(
      () => !!(props.trip?.has_erp_pair && executorLoading.value && !routePointsModel.value.length),
    )

    return {
      MODE_COMPACT,
      TABS,
      activeTab,
      endedAt,
      copyVal,
      dash,
      moneyRange,
      statusClass,
      financeMetrics,
      timelineEvents,
      timelineSegments,
      timelineLegend,
      hoveredIdx,
      timelineColor,
      fmtTs,
      gapLabel,
      eventMeta,
      fmtDate,
      fmtHours,
      fmtMoney,
      fmtPct,
      closeModel,
      isLiveTab,
      liveTabLoading,
      reloadLiveTab,
      loadExecutor,
      executorLoading,
      executorError,
      executorTrip,
      randomExecutor,
      driverFullName,
      brandName,
      managerName,
      loadDowntimes,
      downtimeLoading,
      downtimeError,
      downtimeRows,
      downtimeScale,
      downtimeBar,
      classificationMeta,
      classificationStyle,
      formatOperation,
      formatNaiveDate,
      formatMinutes,
      formatHours,
      loadRouteData,
      routeLoading,
      priceLookup,
      priceLookupError,
      baseRate,
      baseRateForbidden,
      routeTransport,
      TRANSPORT_CHIPS,
      hasCities,
      featuredEntry,
      featuredPrice,
      featuredWithoutNds,
      featuredDerived,
      featuredLabel,
      priceCells,
      totalCost,
      totalCostWithoutNds,
      baseMargin,
      routeMismatch,
      refreshStatusLabel,
      canYandexLink,
      yandexLink,
      fmtPrice,
      fmtRouteDate,
      pureOrder,
      pureOrderError,
      botTask,
      pureOrderAdvise,
      routePointsModel,
      latePointsCount,
      pointsLiveLoading,
      SYNC_SERVICES,
      syncService,
      canViewSync,
      syncLoading,
      syncError,
      syncEntries,
      syncExpanded,
      toggleSyncRow,
      syncLogLink,
      historyEvents,
      historyLoading,
      historyError,
      loadHistory,
    }
  },
}
</script>

<style scoped>
.sync-pane { display: flex; flex-direction: column; gap: 10px; }

.sync-services { display: flex; gap: 6px; }
.sync-chip {
  padding: 3px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  background: #fff;
  color: #475467;
  font-size: 12px;
  cursor: pointer;
}
.sync-chip.active { background: #175cd3; border-color: #175cd3; color: #fff; }

.sync-pre {
  margin: 4px 0 0;
  font-size: 12px;
  color: #912018;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  max-height: 160px;
  overflow: auto;
}
.sync-pre-muted { color: #667085; }

.sync-section-title { font-size: 11px; font-weight: 700; color: #667085; text-transform: uppercase; }

.sync-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.sync-table th {
  text-align: left;
  color: #667085;
  font-weight: 600;
  padding: 4px 8px;
  border-bottom: 1px solid #eaecf0;
}
.sync-table td { padding: 4px 8px; border-bottom: 1px solid #f2f4f7; color: #344054; }
.sync-row { cursor: pointer; }
.sync-row:hover { background: #f9fafb; }
.sync-row-open { background: #f9fafb; }
.sync-caret { width: 18px; color: #98a2b3; user-select: none; }
.sync-row-detail-td { background: #fcfcfd; }
.sync-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 14px;
  font-size: 12px;
  color: #344054;
}
.sync-detail-grid > div { display: flex; gap: 6px; min-width: 0; }
.d-label { color: #667085; min-width: 84px; }
.sync-row-detail .sync-pre { max-height: 220px; }
.sync-detail {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sync-status {
  display: inline-block;
  padding: 0 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.sync-status-success { background: #e6f4ea; color: #1e7e34; }
.sync-status-error { background: #fdeaea; color: #c0392b; }
.sync-status-pending { background: #fff5e0; color: #b7791f; }
.sync-status-info { background: #e8eefc; color: #2563eb; }

.sync-log-link { font-size: 12px; color: #175cd3; text-decoration: none; align-self: flex-start; }
.sync-log-link:hover { text-decoration: underline; }

.t360-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #98a2b3;
  font-size: 13px;
}

.t360-error {
  color: #b42318;
}

.t360-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.t360-route {
  margin: 8px 0;
  font-size: 13px;
  color: #344054;
}

.t360-dates {
  margin-top: 8px;
  font-size: 12px;
  color: #667085;
}

.t360-dates strong {
  color: #344054;
  font-weight: 600;
}

.t360-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.link-chip {
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #f8fafc;
  color: #475467;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  cursor: copy;
  white-space: nowrap;
}

.link-chip:hover {
  background: #eff6ff;
  border-color: #84adff;
  color: #1d4ed8;
}

.t360-section-title {
  margin: 12px 0 4px;
  padding-bottom: 3px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #98a2b3;
  border-bottom: 1px solid #e5e7eb;
}

.t360-section-title:first-child {
  margin-top: 0;
}

.t360-compact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
}

.t360-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 10px;
}

.t360-tab {
  border: none;
  background: none;
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #667085;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.t360-tab:hover {
  color: #0056b3;
}

.t360-tab.active {
  color: #007bff;
  border-bottom-color: #007bff;
}

.t360-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field {
  display: flex;
  gap: 10px;
  font-size: 13px;
  padding: 3px 0;
  border-bottom: 1px dashed #f2f4f7;
}

.f-label {
  flex: 0 0 170px;
  color: #667085;
}

.f-val {
  color: #344054;
  font-weight: 500;
  word-break: break-word;
}

.f-sub {
  margin-left: 4px;
  opacity: 0.6;
  font-size: 11px;
  font-weight: 400;
}

.fin-pane {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fin-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.fin-metric {
  background: #f9fafb;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px 10px;
  text-align: left;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
}

.fin-metric:hover {
  background: #eef4ff;
}

.fin-metric:hover .fin-metric-title {
  color: #1d4ed8;
}

.fin-metric:hover strong {
  color: #1d4ed8;
}

.fin-metric-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  color: #6b7280;
  font-size: 11px;
  margin-bottom: 4px;
}

.fin-metric-title .f-sub {
  margin-left: 0;
}

.fin-metric strong {
  color: #111827;
  font-size: 14px;
}

.fin-metric-soft {
  background: #dcfce7;
  border: 1px solid #86efac;
}

.fin-metric-soft .fin-metric-title {
  color: #15803d;
}

.fin-metric-soft strong {
  color: #166534;
}

.fin-metric-soft:hover {
  background: #bbf7d0;
}

.fin-metric-soft:hover .fin-metric-title {
  color: #14532d;
}

.fin-metric-soft:hover strong {
  color: #14532d;
}

.fin-hint {
  color: #98a2b3;
  font-size: 11px;
  margin: 0;
}

.t360-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 8px;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.badge-on { background: #eef4ff; color: #175cd3; }
.badge-off { background: #f2f4f7; color: #98a2b3; }
.badge-gray { background: #f2f4f7; color: #475467; }
.badge-green { background: #ecfdf3; color: #027a48; }
.badge-red { background: #fef3f2; color: #b42318; }
.badge-purple { background: #f4f0ff; color: #5925dc; }

.tl-wrap {
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
}

.tl-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin: 0 2px 10px;
  flex: none;
}

.tl-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #667085;
  white-space: nowrap;
}

.tl-legend-sq {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: none;
}

.tl-scale {
  position: relative;
  height: 46px;
  margin: 0 2px 12px;
  flex: none;
}

.tl-scale-min,
.tl-scale-max {
  position: absolute;
  top: 24px;
  font-size: 11px;
  color: #98a2b3;
  white-space: nowrap;
}

.tl-scale-min {
  left: 0;
}

.tl-scale-max {
  right: 0;
}

.tl-seg {
  position: absolute;
  top: 9px;
  height: 8px;
  border-radius: 2px;
  opacity: 0.85;
}

.tl-tick {
  position: absolute;
  top: 13px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 2;
}

.tl-tick.active {
  transform: translate(-50%, -50%) scale(1.35);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.18);
}

.tl-item {
  display: flex;
  gap: 10px;
  min-width: 0;
  align-items: stretch;
  padding: 2px 6px;
  margin: 0 -6px;
  border-radius: 6px;
}

.tl-item.hovered {
  background: #f8fafc;
}

.tl-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 14px;
  flex: none;
}

.tl-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  flex: none;
}

.tl-line {
  width: 2px;
  flex: 1;
  background: #e4e7ec;
  min-height: 10px;
}

.tl-content {
  padding-bottom: 14px;
  min-width: 0;
  flex: 1;
}

.tl-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.tl-label {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}

.tl-dt {
  font-size: 12px;
  color: #667085;
  font-variant-numeric: tabular-nums;
}

.tl-gap {
  font-size: 11px;
  color: #98a2b3;
  font-variant-numeric: tabular-nums;
}

.tl-meta {
  font-size: 12px;
  color: #667085;
  margin-top: 2px;
}

.tl-empty {
  padding: 40px 0;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}

/* ── Виджет «Закрытие» (строка-поле, дубль из таблицы рейсов) ── */
.cw-field {
  align-items: center;
}

.cw-field .f-val {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.cw-widget {
  position: relative;
  width: 100%;
  height: 16px;
}

.cw-track {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background: #f2f4f7;
  overflow: hidden;
}

.cw-bar {
  height: 100%;
  border-radius: 4px 0 0 4px;
}

.cw-widget .tl-early { background: #12b76a; color: #12b76a; }
.cw-widget .tl-lastday { background: #f79009; color: #f79009; }
.cw-widget .tl-late { background: #f04438; color: #f04438; }
.cw-widget .tl-neutral { background: #98a2b3; color: #98a2b3; }

.cw-widget .tl-open {
  opacity: 0.55;
  background: repeating-linear-gradient(45deg, currentColor 0 3px, transparent 3px 6px);
}

.cw-mark {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 1px;
  background: #98a2b3;
}

.cw-mark-12 {
  background: #667085;
}

.cw-empty {
  font-size: 12px;
  color: #98a2b3;
}

/* ── Live-вкладки (Исполнитель / Простой) ── */
.t360-tab-refresh {
  margin-left: auto;
  align-self: center;
  border: none;
  background: none;
  padding: 2px 8px;
  font-size: 15px;
  line-height: 1.4;
  color: #667085;
  cursor: pointer;
  border-radius: 6px;
}

.t360-tab-refresh:hover:not(:disabled) {
  color: #007bff;
  background: #f2f4f7;
}

.t360-tab-refresh:disabled {
  opacity: 0.6;
  cursor: default;
}

.t360-tab-refresh.loading {
  animation: t360-spin 1s linear infinite;
}

@keyframes t360-spin {
  to {
    transform: rotate(360deg);
  }
}

.t360-live-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #98a2b3;
  font-size: 13px;
}

.t360-live-empty {
  padding: 32px 8px;
  text-align: center;
  color: #98a2b3;
  font-size: 13px;
}

/* ── «Хронология работы» (таб Распределение) ── */
.t360-history {
  margin-top: 16px;
}

.t360-history-feed {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
}

.t360-history-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #f2f4f7;
  font-size: 12.5px;
}

.t360-history-row .h-time {
  flex: 0 0 auto;
  color: #98a2b3;
  font-variant-numeric: tabular-nums;
}

.t360-history-row .h-src {
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
  background: #f2f4f7;
  color: #475467;
}

.t360-history-row .h-src.src-email {
  background: #fef0ec;
  color: #b42318;
}

.t360-history-row .h-src.src-manual {
  background: #eef4ff;
  color: #175cd3;
}

.t360-history-row .h-text {
  color: #344054;
}

/* ── Вкладка «Простой» ── */
.dt-pane {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dt-scale-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: #98a2b3;
  font-variant-numeric: tabular-nums;
}

.dt-scale-hint {
  flex: 1;
  text-align: center;
}

.dt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.dt-table th {
  text-align: left;
  font-weight: 600;
  color: #667085;
  padding: 4px 6px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.dt-table td {
  padding: 4px 6px;
  border-bottom: 1px dashed #f2f4f7;
  color: #344054;
  vertical-align: middle;
}

.dt-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.dt-warehouse {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dt-tag {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.dt-chart-cell {
  width: 120px;
}

.dt-chart {
  position: relative;
  width: 110px;
  height: 10px;
  border-radius: 3px;
  background: #f2f4f7;
  overflow: hidden;
}

.dt-chart-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: 2px;
  min-width: 2px;
}

.route-pane {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  overflow-y: auto;
}

.pr-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #344054;
}

.pr-dist {
  color: #667085;
  font-weight: 600;
}

.pr-warn {
  padding: 6px 10px;
  border-radius: 8px;
  background: #fffaeb;
  color: #b54708;
  font-size: 12px;
}

.pr-chips {
  display: flex;
  gap: 6px;
}

.pr-chip {
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  background: #f8fafc;
  color: #475467;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 12px;
  cursor: pointer;
}

.pr-chip:hover {
  background: #eff6ff;
  border-color: #84adff;
  color: #1d4ed8;
}

.pr-chip.active {
  background: #eef4ff;
  border-color: #175cd3;
  color: #175cd3;
}

.pr-hero {
  display: flex;
  gap: 12px;
  align-items: stretch;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f9fafb;
}

.pr-hero-derived {
  background: #fffcf5;
}

.pr-hero-main {
  flex: 1 1 auto;
  min-width: 0;
}

.pr-hero-label {
  font-size: 11px;
  color: #98a2b3;
}

.pr-hero-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.pr-hero-unit {
  font-size: 13px;
  font-weight: 500;
  color: #667085;
}

.pr-hero-big {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.pr-hero-sub {
  font-size: 12px;
  color: #667085;
}

.pr-hero-note {
  margin-top: 2px;
  font-size: 11px;
  color: #98a2b3;
}

.pr-hero-total {
  flex: 0 0 auto;
  text-align: right;
  border-left: 1px solid #e5e7eb;
  padding-left: 12px;
}

.pr-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.pr-cell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
}

.pr-cell-derived {
  background: #fffcf5;
}

.pr-cell-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}

.pr-cell-tag {
  font-size: 10px;
  font-weight: 500;
  color: #98a2b3;
}

.pr-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  color: #667085;
  font-variant-numeric: tabular-nums;
}

.pr-line span {
  flex: 0 0 32px;
  font-size: 11px;
}

.pr-line b {
  font-size: 13px;
  color: #111827;
}

.pr-line small {
  color: #98a2b3;
}

.pr-cell-empty {
  font-size: 12px;
  color: #98a2b3;
}

.pr-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 12px;
  color: #667085;
}

.pr-link {
  color: #1d4ed8;
  text-decoration: none;
  font-weight: 600;
}

.pr-link:hover {
  text-decoration: underline;
}

.pr-refresh {
  font-size: 12px;
  color: #667085;
}

.pr-base {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.pr-base-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f9fafb;
  min-width: 200px;
}

.pr-base-item b {
  font-size: 15px;
  color: #111827;
}

.pr-margin-pos { color: #027a48; }
.pr-margin-neg { color: #b42318; }

.pr-task {
  font-size: 13px;
  color: #344054;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rp-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rp-title-block {
  flex: 1 1 auto;
  border-bottom: 0;
  margin: 0;
}

.rp-dt-btn {
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #f8fafc;
  color: #475467;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  cursor: pointer;
}

.rp-dt-btn:hover {
  background: #fef3f2;
  border-color: #f04438;
  color: #b42318;
}

.rp-item {
  display: flex;
  gap: 10px;
}

.rp-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 12px;
  padding-top: 3px;
}

.rp-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
}

.rp-dot-load { background: #175cd3; }
.rp-dot-unload { background: #d92d20; }

.rp-line {
  width: 2px;
  flex: 1 1 auto;
  background: #e5e7eb;
  margin-top: 2px;
}

.rp-content {
  flex: 1 1 auto;
  min-width: 0;
  padding-bottom: 12px;
}

.rp-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.rp-title {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}

.rp-plan {
  font-size: 12px;
  color: #667085;
  font-variant-numeric: tabular-nums;
}

.rp-meta {
  font-size: 12px;
  color: #667085;
  word-break: break-word;
}

.rp-fact {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 4px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #f9fafb;
  font-size: 12px;
  color: #667085;
}

.rp-fact-item b {
  color: #344054;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
</style>

<style>
/* Статус-пилюля 360-обзора — общая для попапа, standalone-страницы и контента. */
.t360-status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.t360-pill-open { background: #eff6ff; color: #1d4ed8; }
.t360-pill-closed { background: #ecfdf3; color: #027a48; }
.t360-pill-gray { background: #f2f4f7; color: #475467; }
.t360-pill-red { background: #fef3f2; color: #b42318; }
.t360-pill-orange { background: #fffaeb; color: #b54708; }
</style>
